// DS: TA0.2M
import type { FC } from 'react';

import { useCallback, useRef } from 'react';
import classnames from 'classnames';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { ProfileOwnerSubscribeButton } from '@wix/da-shared-react/pkg/Button';
import AvatarBirthdayBadge from '../ProfileIdentity/_partials/AvatarBirthdayBadge';
import JoinGroupButton from '@wix/da-shared-react/pkg/JoinGroupButton';
import MoreActionsDropdownMobile from '../ProfileIdentity/_partials/MoreActionsDropdown/Mobile';
import MonetizationPromoBanner from '../MonetizationPromoBanner';
import WatchButton from './WatchButton';
import SendMessageButton from '../../SubNavBar/Desktop/variants/UserProfileSubNavDesktopInner/_partials/SendMessageButton/SendMessageButton';
import SkinButton from './SkinButton';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import ProfileStatItem from '../ProfileIdentity/_partials/ProfileStatItem';
import ProfileStats from '../ProfileIdentity/_partials/ProfileStats';
import AvatarEdit from '../ProfileIdentity/_partials/AvatarEdit';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { useHeroImageForContainer } from '@wix/da-shared-react/pkg/utils/hooks/useHeroImageForContainer';
import { setIsEditorOpen } from '../../../actions/avatar';
import SubmitButton from '../../SubNavBar/Desktop/variants/GroupProfileSubNavDesktopInner/_partials/SubmitButton';
import { Url } from '@wix/da-url';

import s from './ProfileHeroMobile.scss';

const GROUP_DEFAULT_HERO_IMAGE = Url.staticCDNLink(
  'eclipse/empty-states/group-cover-image-mobile.png'
);
const GROUP_DEFAULT_HERO_STYLES = {
  backgroundImage: `url(${GROUP_DEFAULT_HERO_IMAGE})`,
  backgroundSize: 'cover',
};

export interface Props {
  className?: string;
  coverDeviation?: PapiDeviation;
  profileOwnerUser: PapiUser;
  canChangeAvatar: boolean;
  isAuthorizedToCustomize: boolean;
  isOwner: boolean;
  hasSubscriptions: boolean;
  isGroup: boolean;
  isContactable?: boolean;
  stats: object;
  tagline?: string;
  openAvatarEditor: typeof setIsEditorOpen;
}

export const ProfileHeroMobile: FC<Props> = ({
  className,
  stats,
  tagline = '',
  coverDeviation,
  profileOwnerUser,
  isAuthorizedToCustomize,
  canChangeAvatar,
  isOwner,
  isGroup,
  hasSubscriptions,
  isContactable = true,
  openAvatarEditor,
}) => {
  const isDefaultAvatar =
    profileOwnerUser?.usericonDefault || !profileOwnerUser?.usericon;
  const showSubscribeButton = hasSubscriptions && !isOwner;

  const showIconOnlyNotes = hasSubscriptions && !profileOwnerUser.isSubscribed;
  const hideWatchButton = profileOwnerUser.isSubscribed;
  const showHeadStats = useFlag('profile_head_stats') && !isGroup;

  const showStats = isGroup
    ? ['founded', 'members', 'watchers']
    : ['watchers', 'deviations', 'pageviews'];

  const rootDivRef = useRef<HTMLDivElement>(null);
  const { hero } = useHeroImageForContainer(coverDeviation, rootDivRef, {
    isViewportWidth: true,
    width: 1024,
    height: 160,
    mobileWidth: 412,
    mobileHeight: 160,
  });
  const openEditor = useCallback(
    e => {
      canChangeAvatar && openAvatarEditor(true);
      e.preventDefault();
    },
    [canChangeAvatar, openAvatarEditor]
  );

  return (
    <div
      className={classnames(
        s['root'],
        className,
        isGroup && s['group-profile'],
        showHeadStats && s['head-stats-layout']
      )}
    >
      <div className={s['hero-wrapper']} ref={rootDivRef}>
        {coverDeviation ? (
          <div
            className={classnames(s['background'], s['hero'])}
            style={{
              backgroundImage: hero ? `url(${hero.src})` : undefined,
            }}
          >
            {hero && (
              <img
                src={hero.src}
                alt=""
                width="0"
                height="0"
                style={{ display: 'none' }}
              />
            )}
          </div>
        ) : (
          <div
            className={classnames(
              s['background'],
              s['no-image'],
              !isGroup && s['default']
            )}
            style={isGroup ? GROUP_DEFAULT_HERO_STYLES : undefined}
          />
        )}
        <MonetizationPromoBanner className={s['tier-banner']} />
        {!showHeadStats && !isGroup && !isOwner && (
          <MoreActionsDropdownMobile className={s['more-menu']} />
        )}
      </div>
      {isAuthorizedToCustomize && isGroup && (
        <SkinButton className={s['skin-button']} />
      )}
      {showHeadStats ? (
        <>
          <div className={classnames(s['identity-row'], !coverDeviation && s['no-cover'])}>
            <div className={s['avatar']}>
              {profileOwnerUser.isBirthweek && <AvatarBirthdayBadge />}
              <div className={s['avatar-image']} onClick={openEditor}>
                <UserAvatar
                  user={profileOwnerUser}
                  size={72}
                  className={classnames(
                    s['user-avatar'],
                    canChangeAvatar && isDefaultAvatar && s['outline']
                  )}
                />
              </div>
              {canChangeAvatar && <AvatarEdit className={s['avatar-edit-sm']} />}
            </div>
            <div className={s['info-text']}>
              <div className={s['username-skin-button-wrapper']}>
                <UserLink className={s['username']} user={profileOwnerUser} />
                {isAuthorizedToCustomize && (
                  <SkinButton className={s['skin-button']} />
                )}
                {!isOwner && (
                  <MoreActionsDropdownMobile className={s['more-menu-inline']} />
                )}
              </div>
              <div className={s['tagline']}>{tagline}</div>
            </div>
          </div>
          <div className={s['hug']}>
            <ProfileStats {...stats} />
            {!isOwner && (
              <div className={s['action-bar']}>
                <SendMessageButton
                  profileOwner={profileOwnerUser}
                  className={classnames(
                    s['cta-button'],
                    showIconOnlyNotes && s['cta-button-icon-only-damz']
                  )}
                  iconOnly={showIconOnlyNotes}
                  isContactable={isContactable}
                />
                {!hideWatchButton && <WatchButton user={profileOwnerUser} />}
                {showSubscribeButton && (
                  <ProfileOwnerSubscribeButton
                    user={profileOwnerUser}
                    className={s['cta-button']}
                  />
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className={s['avatar']}>
            {profileOwnerUser.isBirthweek && <AvatarBirthdayBadge />}
            <div className={s['avatar-image']} onClick={openEditor}>
              <UserAvatar
                user={profileOwnerUser}
                size={profileOwnerUser.isGroup ? 50 : 100}
                className={classnames(
                  s['user-avatar'],
                  canChangeAvatar && isDefaultAvatar && s['outline']
                )}
              />
            </div>
            {canChangeAvatar && <AvatarEdit />}
          </div>
          <div className={s['hug']}>
            <div className={s['info-text']}>
              <div className={s['username-skin-button-wrapper']}>
                <UserLink className={s['username']} user={profileOwnerUser} />
                {isAuthorizedToCustomize && !isGroup && (
                  <SkinButton className={s['skin-button']} />
                )}
              </div>
              <div className={s['tagline']}>{tagline}</div>
            </div>
            {!isGroup && !isOwner && (
              <div className={s['action-bar']}>
                <SendMessageButton
                  profileOwner={profileOwnerUser}
                  className={classnames(
                    s['cta-button'],
                    showIconOnlyNotes && s['cta-button-icon-only-damz']
                  )}
                  iconOnly={showIconOnlyNotes}
                  isContactable={isContactable}
                />
                {!hideWatchButton && <WatchButton user={profileOwnerUser} />}
                {showSubscribeButton && (
                  <ProfileOwnerSubscribeButton
                    user={profileOwnerUser}
                    className={s['cta-button']}
                  />
                )}
              </div>
            )}
            {isGroup && (
              <div className={s['group-action-bar']}>
                <JoinGroupButton
                  type="solid"
                  group={profileOwnerUser}
                  className={s['cta-button']}
                />
                <SubmitButton className={s['cta-button']} />
                <MoreActionsDropdownMobile />
              </div>
            )}
            <div className={s['stats']}>
              {showStats.map(stat => (
                <ProfileStatItem
                  key={stat}
                  stat={stat}
                  value={stats[stat]}
                  className={s['stat-item']}
                  countClassName={s['stat-item-count']}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
ProfileHeroMobile.displayName = 'ProfileHeroMobile';

export default ProfileHeroMobile;
